import { Calendar, Gauge, MinusCircle, Percent, TrendingDown, TrendingUp } from "lucide-react";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type { EarningsCalendarItemOut } from "@/api/client";

/**
 * Four prediction cards for a selected earnings event.
 *
 * Predictions are stubbed: no model has run against this earnings event yet
 * (Step 3 of the roadmap). Cards render placeholder content but keep the
 * layout and tokens that will be reused once real prediction data is wired in.
 */
interface PredictionView {
  direction: "up" | "down" | null;
  expectedPct: number | null;
  confidence: number | null;
  predictionRunDate: string | null;
  trainingRange: string | null;
}

const STUB_PREDICTION: PredictionView = {
  direction: null,
  expectedPct: null,
  confidence: null,
  predictionRunDate: null,
  trainingRange: null,
};

export function EarningsDetailPanel({ event }: { event: EarningsCalendarItemOut }) {
  const { direction, expectedPct, confidence, predictionRunDate, trainingRange } =
    STUB_PREDICTION;

  return (
    <div className="border-t border-border bg-muted/20 px-3 py-3 space-y-3">
      <div className="flex items-baseline justify-between gap-3">
        <div className="flex items-baseline gap-2 min-w-0">
          <div className="font-mono font-semibold text-[13px] whitespace-nowrap">
            {event.primary_symbol ?? "—"}
          </div>
          <div className="text-[12px] text-muted-foreground truncate">{event.company_name}</div>
        </div>
        <div className="text-[11px] font-mono text-muted-foreground whitespace-nowrap">
          {formatLongDate(event.announcement_date)}
          {event.fiscal_period ? ` · ${event.fiscal_period}` : ""}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
        <PredictionCard
          icon={
            direction === "up" ? (
              <TrendingUp className="h-4 w-4 text-[hsl(var(--chart-up))]" />
            ) : direction === "down" ? (
              <TrendingDown className="h-4 w-4 text-[hsl(var(--chart-down))]" />
            ) : (
              <MinusCircle className="h-4 w-4 text-muted-foreground" />
            )
          }
          label="Direction"
          value={direction === "up" ? "Up" : direction === "down" ? "Down" : "No prediction"}
          valueClass={
            direction === "up"
              ? "text-[hsl(var(--chart-up))]"
              : direction === "down"
                ? "text-[hsl(var(--chart-down))]"
                : "text-muted-foreground"
          }
        />
        <PredictionCard
          icon={<Percent className="h-4 w-4 text-muted-foreground" />}
          label="Expected change"
          value={expectedPct === null ? "—" : `${expectedPct >= 0 ? "+" : ""}${expectedPct.toFixed(2)}%`}
        />
        <PredictionCard
          icon={<Gauge className="h-4 w-4 text-muted-foreground" />}
          label="Confidence"
          value={confidence === null ? "—" : `${(confidence * 100).toFixed(0)}%`}
        />
        <PredictionCard
          icon={<Calendar className="h-4 w-4 text-muted-foreground" />}
          label="Prediction run"
          value={predictionRunDate ?? "—"}
          subValue={trainingRange ?? "no training window"}
        />
      </div>
      <div className="text-[10px] text-muted-foreground font-mono">
        Model output pending — no prediction model has run against this announcement yet.
      </div>
    </div>
  );
}

function PredictionCard({
  icon,
  label,
  value,
  subValue,
  valueClass,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  subValue?: string;
  valueClass?: string;
}) {
  return (
    <Card className="px-3 py-2 flex flex-col gap-1">
      <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-muted-foreground font-mono">
        <span>{label}</span>
        {icon}
      </div>
      <div className={cn("font-mono text-[15px] tabular-nums font-semibold", valueClass)}>
        {value}
      </div>
      {subValue && <div className="text-[10px] text-muted-foreground font-mono">{subValue}</div>}
    </Card>
  );
}

function formatLongDate(iso: string): string {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
